/**
 * Refund Policy Page
 * Complete refund policy for Rely platform
 */

import React from 'react';
import { MdRefresh } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import LegalPageLayout from '../../components/legal/LegalPageLayout';
import {
  LegalSection,
  LegalParagraph,
  LegalList,
  LegalDisclaimer,
  LegalTable,
  LegalSubSection,
  LegalContact,
  LegalDivider,
} from '../../components/legal/LegalSection';
import { LEGAL_CONFIG } from '../../config/legal';

const RefundPage = () => {
  const { t } = useTranslation();

  return (
    <LegalPageLayout
      title={t('refund.title', 'Política de Reembolsos')}
      subtitle={t(
        'refund.subtitle',
        'Condiciones y proceso para solicitar reembolsos'
      )}
      documentType="refund"
      icon={MdRefresh}
    >
      {/* Summary Box */}
      <LegalDisclaimer type="info" title={t('refund.summary.title', 'Resumen')}>
        {t(
          'refund.summary.text',
          `Los reembolsos están disponibles antes de que se envíe el filing al estado, sujeto a una tarifa de procesamiento de $${LEGAL_CONFIG.refundPolicy.processingFee}. Las tarifas gubernamentales no son reembolsables.`
        )}
      </LegalDisclaimer>

      {/* Introduction */}
      <LegalParagraph>
        {t(
          'refund.intro',
          `${LEGAL_CONFIG.company.name} se compromete a proporcionar servicios de alta calidad. Entendemos que las circunstancias pueden cambiar, por lo que ofrecemos reembolsos bajo ciertas condiciones.`
        )}
      </LegalParagraph>

      {/* Section 1: Refund Eligibility */}
      <LegalSection
        number={1}
        title={t('refund.eligibility.title', 'Elegibilidad para Reembolsos')}
        id="eligibility"
      >
        <LegalParagraph>
          {t(
            'refund.eligibility.intro',
            'Su elegibilidad para un reembolso depende del estado de su orden:'
          )}
        </LegalParagraph>
        <LegalTable
          headers={[
            t('refund.table.plan', 'Plan'),
            t('refund.table.before', 'Antes del Filing'),
            t('refund.table.after', 'Despues del Filing'),
          ]}
          rows={[
            [
              `${LEGAL_CONFIG.plans.essential.name} ($${LEGAL_CONFIG.plans.essential.price})`,
              t(
                'refund.eligibility.beforeFiling',
                `100% - $${LEGAL_CONFIG.refundPolicy.processingFee} tarifa`
              ),
              t('refund.eligibility.afterFiling', 'No reembolsable'),
            ],
            [
              `${LEGAL_CONFIG.plans.business.name} ($${LEGAL_CONFIG.plans.business.price})`,
              t(
                'refund.eligibility.beforeFiling',
                `100% - $${LEGAL_CONFIG.refundPolicy.processingFee} tarifa`
              ),
              t('refund.eligibility.afterFiling', 'No reembolsable'),
            ],
            [
              `${LEGAL_CONFIG.plans.business_plus.name} ($${LEGAL_CONFIG.plans.business_plus.price})`,
              t(
                'refund.eligibility.beforeFiling',
                `100% - $${LEGAL_CONFIG.refundPolicy.processingFee} tarifa`
              ),
              t('refund.eligibility.afterFiling', 'No reembolsable'),
            ],
          ]}
        />
      </LegalSection>

      {/* Section 2: What Can Be Refunded */}
      <LegalSection
        number={2}
        title={t('refund.what.title', 'Que se Puede Reembolsar')}
        id="what-refundable"
      >
        <LegalSubSection title={t('refund.what.yes.title', 'Reembolsable')}>
          <LegalList
            items={[
              t(
                'refund.what.yes.service',
                'Tarifas de servicio de Rely (antes del filing)'
              ),
              t(
                'refund.what.yes.agent',
                'Servicio de Agente Registrado (si aun no se ha activado)'
              ),
              t('refund.what.yes.addons', 'Addons no utilizados'),
            ]}
          />
        </LegalSubSection>
        <LegalSubSection title={t('refund.what.no.title', 'No Reembolsable')}>
          <LegalList
            items={[
              t(
                'refund.what.no.government',
                'Tarifas gubernamentales estatales'
              ),
              t(
                'refund.what.no.filed',
                'Servicios despues de que se haya enviado el filing'
              ),
              t(
                'refund.what.no.ein',
                'Tramites de EIN una vez iniciados con el IRS'
              ),
              t(
                'refund.what.no.documents',
                'Documentos ya generados y entregados'
              ),
            ]}
          />
        </LegalSubSection>
      </LegalSection>

      {/* Section 3: Financial Services */}
      <LegalSection
        number={3}
        title={t(
          'refund.financial.title',
          'Servicios Financieros (Asistencia Bancaria)'
        )}
        id="financial-services"
      >
        <LegalDisclaimer
          type="info"
          title={t('refund.financial.disclaimer.title', 'Importante')}
        >
          {t(
            'refund.financial.disclaimer.text',
            LEGAL_CONFIG.financialServices.disclaimer.es
          )}
        </LegalDisclaimer>

        <LegalParagraph>
          {t(
            'refund.financial.intro',
            'Rely ofrece asistencia para aplicar a servicios financieros con los siguientes proveedores:'
          )}
        </LegalParagraph>

        <LegalList
          items={[
            t(
              'refund.financial.banks',
              `Bancos: ${LEGAL_CONFIG.financialServices.providers.banks.join(
                ', '
              )}`
            ),
            t(
              'refund.financial.payments',
              `Procesadores de pago: ${LEGAL_CONFIG.financialServices.providers.paymentProcessors.join(
                ', '
              )}`
            ),
            t(
              'refund.financial.crypto',
              `Exchanges de criptomonedas: ${LEGAL_CONFIG.financialServices.providers.cryptoExchanges.join(
                ', '
              )}`
            ),
          ]}
        />

        <LegalSubSection
          title={t(
            'refund.financial.rejection.title',
            'Reembolso por Rechazo de Solicitud'
          )}
        >
          <LegalParagraph>
            {t(
              'refund.financial.rejection.description',
              LEGAL_CONFIG.financialServices.refundOnRejection.description.es
            )}
          </LegalParagraph>

          <LegalTable
            headers={[
              t('refund.financial.table.scenario', 'Escenario'),
              t('refund.financial.table.refund', 'Reembolso'),
            ]}
            rows={[
              [
                t('refund.financial.table.approved', 'Solicitud aprobada'),
                t('refund.financial.table.approved.refund', 'No aplica'),
              ],
              [
                t(
                  'refund.financial.table.rejected',
                  'Solicitud rechazada por el proveedor'
                ),
                t(
                  'refund.financial.table.rejected.refund',
                  `${LEGAL_CONFIG.financialServices.refundOnRejection.percentage}% del monto pagado`
                ),
              ],
              [
                t(
                  'refund.financial.table.cancelled',
                  'Cancelacion antes de enviar solicitud'
                ),
                t(
                  'refund.financial.table.cancelled.refund',
                  `100% - $${LEGAL_CONFIG.refundPolicy.processingFee} tarifa`
                ),
              ],
            ]}
          />

          <LegalParagraph>
            {t(
              'refund.financial.processing',
              `El reembolso por rechazo se procesa en ${LEGAL_CONFIG.financialServices.refundOnRejection.processingTime} una vez confirmado el rechazo por parte del proveedor.`
            )}
          </LegalParagraph>
        </LegalSubSection>

        <LegalDisclaimer
          type="warning"
          title={t(
            'refund.financial.noguarantee.title',
            'Sin Garantia de Aprobacion'
          )}
        >
          {t(
            'refund.financial.noguarantee.text',
            'La aprobacion de cualquier solicitud financiera depende exclusivamente del proveedor. Rely no puede garantizar la aprobacion y no es responsable por rechazos basados en los criterios internos del proveedor.'
          )}
        </LegalDisclaimer>
      </LegalSection>

      {/* Section 4: Processing Fee */}
      <LegalSection
        number={4}
        title={t('refund.fee.title', 'Tarifa de Procesamiento')}
        id="processing-fee"
      >
        <LegalParagraph>
          {t(
            'refund.fee.description',
            `Todas las solicitudes de reembolso estan sujetas a una tarifa de procesamiento de $${LEGAL_CONFIG.refundPolicy.processingFee} USD. Esta tarifa cubre los costos administrativos asociados con el procesamiento del reembolso.`
          )}
        </LegalParagraph>
        <LegalDisclaimer
          type="info"
          title={t('refund.fee.exception.title', 'Excepcion')}
        >
          {t(
            'refund.fee.exception.text',
            'Si el reembolso se debe a un error de nuestra parte, no se cobrara la tarifa de procesamiento.'
          )}
        </LegalDisclaimer>
      </LegalSection>

      {/* Section 5: How to Request */}
      <LegalSection
        number={5}
        title={t('refund.how.title', 'Como Solicitar un Reembolso')}
        id="how-to-request"
      >
        <LegalParagraph>
          {t(
            'refund.how.intro',
            'Para solicitar un reembolso, siga estos pasos:'
          )}
        </LegalParagraph>
        <LegalList
          items={[
            t(
              'refund.how.step1',
              `Envie un correo a ${LEGAL_CONFIG.contact.billing} con el asunto "Solicitud de Reembolso"`
            ),
            t(
              'refund.how.step2',
              'Incluya su número de orden y dirección de correo registrada'
            ),
            t(
              'refund.how.step3',
              'Explique brevemente el motivo de la solicitud'
            ),
            t(
              'refund.how.step4',
              `Espere nuestra respuesta dentro de ${LEGAL_CONFIG.refundPolicy.responseTime}`
            ),
          ]}
        />
      </LegalSection>

      {/* Section 6: Processing Time */}
      <LegalSection
        number={6}
        title={t('refund.time.title', 'Tiempo de Procesamiento')}
        id="processing-time"
      >
        <LegalParagraph>
          {t(
            'refund.time.description',
            'Una vez aprobada su solicitud de reembolso:'
          )}
        </LegalParagraph>
        <LegalList
          items={[
            t(
              'refund.time.response',
              `Respuesta inicial: ${LEGAL_CONFIG.refundPolicy.responseTime}`
            ),
            t(
              'refund.time.processing',
              `Procesamiento del reembolso: ${LEGAL_CONFIG.refundPolicy.processingTime}`
            ),
            t(
              'refund.time.credit',
              'El reembolso se acreditará al método de pago original'
            ),
          ]}
        />
        <LegalParagraph>
          {t(
            'refund.time.bank',
            'El tiempo adicional para que los fondos aparezcan en su cuenta depende de su institucion financiera.'
          )}
        </LegalParagraph>
      </LegalSection>

      {/* Section 7: Partial Refunds */}
      <LegalSection
        number={7}
        title={t('refund.partial.title', 'Reembolsos Parciales')}
        id="partial-refunds"
      >
        <LegalParagraph>
          {t(
            'refund.partial.description',
            'En algunos casos, podemos ofrecer reembolsos parciales cuando:'
          )}
        </LegalParagraph>
        <LegalList
          items={[
            t(
              'refund.partial.service',
              'Parte del servicio ya se ha completado'
            ),
            t(
              'refund.partial.addon',
              'Solo algunos addons son elegibles para reembolso'
            ),
            t(
              'refund.partial.goodwill',
              'Como gesto de buena voluntad en circunstancias especiales'
            ),
          ]}
        />
      </LegalSection>

      {/* Section 8: Disputes */}
      <LegalSection
        number={8}
        title={t('refund.disputes.title', 'Disputas y Chargebacks')}
        id="disputes"
      >
        <LegalDisclaimer
          type="warning"
          title={t('refund.disputes.warning.title', 'Importante')}
        >
          {t(
            'refund.disputes.warning.text',
            'Por favor contactenos antes de iniciar una disputa con su tarjeta de credito. Estamos comprometidos a resolver cualquier problema directamente.'
          )}
        </LegalDisclaimer>
        <LegalParagraph>
          {t(
            'refund.disputes.description',
            'Si tiene un problema con su pedido o pago:'
          )}
        </LegalParagraph>
        <LegalList
          items={[
            t(
              'refund.disputes.contact',
              `Primero, contacte a ${LEGAL_CONFIG.contact.billing}`
            ),
            t(
              'refund.disputes.response',
              'Le responderemos dentro de 1-2 días hábiles'
            ),
            t(
              'refund.disputes.resolve',
              'Trabajaremos con usted para encontrar una solución justa'
            ),
          ]}
        />
        <LegalParagraph>
          {t(
            'refund.disputes.chargeback',
            'Los chargebacks fraudulentos o injustificados pueden resultar en la suspension de su cuenta y acciones legales.'
          )}
        </LegalParagraph>
      </LegalSection>

      {/* Section 9: Special Circumstances */}
      <LegalSection
        number={9}
        title={t('refund.special.title', 'Circunstancias Especiales')}
        id="special-circumstances"
      >
        <LegalParagraph>
          {t(
            'refund.special.intro',
            'Consideramos reembolsos fuera de la politica estandar en casos de:'
          )}
        </LegalParagraph>
        <LegalList
          items={[
            t(
              'refund.special.error',
              'Errores de nuestra parte en el procesamiento'
            ),
            t('refund.special.duplicate', 'Cobros duplicados'),
            t(
              'refund.special.technical',
              'Problemas técnicos que impidieron el servicio'
            ),
            t('refund.special.death', 'Fallecimiento del solicitante'),
          ]}
        />
        <LegalParagraph>
          {t(
            'refund.special.contact',
            'Para estas situaciones, contactenos directamente y revisaremos su caso individualmente.'
          )}
        </LegalParagraph>
      </LegalSection>

      <LegalDivider />

      {/* Contact Information */}
      <LegalSection
        title={t('refund.contact.title', 'Contacto para Reembolsos')}
        id="contact"
      >
        <LegalParagraph>
          {t(
            'refund.contact.text',
            'Para solicitar un reembolso o resolver problemas de facturación:'
          )}
        </LegalParagraph>
        <LegalContact
          label={t('refund.contact.billing', 'Facturación')}
          email={LEGAL_CONFIG.contact.billing}
          description={t(
            'refund.contact.description',
            'Para solicitudes de reembolso y consultas de facturación'
          )}
        />
      </LegalSection>
    </LegalPageLayout>
  );
};

export default RefundPage;
